import { create } from 'zustand';
import { api } from '../api';
import type { DrawerSummary } from '../types';

interface DrawerState {
  activeSessionId: string | null;
  loading: boolean;
  error: string | null;
  summary: DrawerSummary | null;

  hydrate(): Promise<void>;
  open(openingFloat: string, note?: string): Promise<void>;
  close(countedAmount: string, note?: string): Promise<void>;
  movement(type: 'cash_in' | 'cash_out' | 'drop', amount: string, note?: string): Promise<void>;
  refreshSummary(): Promise<void>;
}

export const useDrawerStore = create<DrawerState>((set, get) => ({
  activeSessionId: null,
  loading: false,
  error: null,
  summary: null,

  async hydrate() {
    set({ loading: true });
    try {
      const id = await api.drawer.activeSessionId();
      // Clear stale summary if the server says the session is gone — otherwise
      // the other app's "Open shift" wizard renders behind a leftover summary
      // until refreshSummary runs.
      set({
        activeSessionId: id,
        summary: id && get().summary?.sessionId === id ? get().summary : null,
        loading: false,
        error: null,
      });
    } catch (e) {
      set({ loading: false, error: (e as Error).message });
    }
  },

  async open(openingFloat, note) {
    set({ loading: true, error: null });
    try {
      const session = await api.drawer.open(openingFloat, note);
      set({ activeSessionId: session.id, summary: null, loading: false });
      await get().refreshSummary();
    } catch (e) {
      set({ loading: false, error: (e as Error).message });
      throw e;
    }
  },

  async close(countedAmount, note) {
    set({ loading: true, error: null });
    try {
      await api.drawer.close(countedAmount, note);
      set({ activeSessionId: null, summary: null, loading: false });
    } catch (e) {
      set({ loading: false, error: (e as Error).message });
      throw e;
    }
  },

  async movement(type, amount, note) {
    await api.drawer.movement(type, amount, note);
    await get().refreshSummary();
  },

  async refreshSummary() {
    try {
      const summary = await api.drawer.summary();
      if (summary) set({ summary, error: null });
    } catch (e) {
      // Keep showing a previous summary if we have one; only surface the
      // error when there's nothing else to render — otherwise the Shift page
      // sits on "Loading shift…" forever with no explanation.
      if (!get().summary) set({ error: (e as Error).message });
    }
  },
}));
